/**
 * §10's two full-width summary panels — `Tech Table Appointments` and `Umpires Appointments`.
 *
 * Both print `completed / appointed` in every count cell, sorted by appointed then completed,
 * with the official's name in their gender colour.
 *
 * **RUN 53 (Alfred, 2026-09-03) — the workload is now the WHOLE TOURNAMENT'S.**
 *
 * Superseded, and left here because it is the thing a reader will assume is still true: these
 * panels used to count only the days the desk happened to hold — the focus day plus the
 * lookahead page — and captioned themselves `N Match Days In View`. `GET /appointments` takes
 * a day, the desk fetched one call per day on screen, and these tables summed exactly those
 * views. That was internally honest and practically wrong: a Technical Delegate who has
 * drafted appointments across the whole schedule reads this table as the tournament's answer,
 * and a four-day slice reporting `0/1` against a full draft is a defect, not a caption
 * problem.
 *
 * Both routes have always answered the wider question without a day on them — `GET
 * /appointments` and `GET /umpire-appointments` with no parameters are schedule-wide reads,
 * measured over every stored row, drafts included. So the desk now makes exactly ONE extra
 * read per panel, on load, and these two tables are fed from those alone. The per-day reads
 * that drive everything else on the desk — the status band, the crew sheets, the lookahead —
 * are untouched, and nothing here feeds them.
 *
 * The `TO Appointments` matrix stays linked from the foot: it is the per-day detail view, and
 * remains the place to see who is on which match.
 *
 * **RUN 53 — the Umpires panel now has its store.** Superseding the note that said it did not:
 * module 02 is built (`UmpireAppointmentsPage`, `GET /umpire-appointments`), so §10's
 * `FU / RU / VU` table is drawn from the Umpires Managers' own half (`tmsActual`, §B1/§B4)
 * rather than an empty state naming the module that owed it. The advisory half is a yardstick
 * that is never pushed (§B9) and is deliberately not counted here.
 *
 * **Data is real as of 2026-09-02** — superseding the `Fictional data only` note this file
 * carried. Nothing is invented in this module either way: it renders rows it is handed.
 */

import { Link } from "react-router-dom";

import { EmptyState, FailBanner, Skeleton } from "../../app/states.js";
import { Tabs, type TabOption } from "../appointments/Tabs.js";
import { genderClass, type UmpireWorkloadRow, type WorkloadRow } from "./derive.js";

export type TechRoleFilter = "all" | "TO" | "Judge";

/** §10's tabs, verbatim: `All` / `TO` / `Judge`. */
export const TECH_ROLE_TABS: readonly TabOption<TechRoleFilter>[] = [
  { value: "all", label: "All" },
  { value: "TO", label: "TO" },
  { value: "Judge", label: "Judge" },
];

export type UmpireGenderFilter = "all" | "m" | "f";

/** §10's second set of tabs, verbatim: `All` / `Male` / `Female`. */
export const UMPIRE_GENDER_TABS: readonly TabOption<UmpireGenderFilter>[] = [
  { value: "all", label: "All" },
  { value: "m", label: "Male" },
  { value: "f", label: "Female" },
];

/** §10 fixes ten rows a page. */
export const ROWS_PER_PAGE = 10;

/**
 * What both panels caption themselves with now that both are schedule-wide.
 *
 * `Whole Tournament` and not `All 13 Match Days`: the count was only ever there to warn that
 * the figure was partial, and a number in that slot invites the reader to work out whether it
 * is the whole schedule. The words say it outright.
 */
export const TOURNAMENT_SCOPE_LABEL = "Whole Tournament";

/**
 * The panel-scoped loading and failure states §B9 requires, shared by both summaries.
 *
 * Scoped to the panel deliberately: these two reads are `requireTd`, and a Umpires Manager who
 * reached the desk gets a 401 on them. One refused summary must not take the desk down with
 * it, so the banner sits inside the section and the rest of the page goes on drawing.
 */
function PanelState({
  loading,
  failure,
}: {
  loading: boolean;
  failure: Error | null;
}): React.JSX.Element | null {
  if (failure !== null) return <FailBanner error={failure} />;
  if (loading) return <Skeleton rows={6} />;
  return null;
}

function Count({ done, appointed }: { done: number; appointed: number }): React.JSX.Element {
  return (
    <td className={appointed === 0 && done === 0 ? "cell-cnt z" : "cell-cnt"}>
      {done}/{appointed}
    </td>
  );
}

export function TechTableSummary({
  rows,
  role,
  onRole,
  page,
  onPage,
  loading = false,
  failure = null,
}: {
  rows: readonly WorkloadRow[];
  role: TechRoleFilter;
  onRole: (role: TechRoleFilter) => void;
  page: number;
  onPage: (page: number) => void;
  /** The schedule-wide read is still in flight. §B9: draw bars, never a figure. */
  loading?: boolean;
  /** The schedule-wide read was refused or failed. Banner inside the panel, not the page. */
  failure?: Error | null;
}): React.JSX.Element {
  const filtered = rows.filter((row) => role === "all" || row.role === role);
  const pageCount = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const current = Math.min(page, pageCount);
  const shown = filtered.slice((current - 1) * ROWS_PER_PAGE, current * ROWS_PER_PAGE);
  const pending = loading || failure !== null;

  return (
    <section className="panel" aria-labelledby="techsumTitle">
      <div className="panel__head">
        <h3 className="panel__title" id="techsumTitle">
          Tech Table Appointments
        </h3>
        <span className="panel__note">{TOURNAMENT_SCOPE_LABEL}</span>
        <span className="spacer" />
        <Tabs
          label="Tech table role filter"
          options={TECH_ROLE_TABS}
          value={role}
          onChange={onRole}
        />
      </div>

      {pending ? (
        <PanelState loading={loading} failure={failure} />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No Technical Officials"
          detail="No Technical Officer or Judge is on the roster for this tournament, so there is no workload to summarise."
        />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="mtable" aria-label="Technical official appointments">
            <thead>
              <tr>
                <th scope="col">Official Name</th>
                <th scope="col">Ctry</th>
                <th scope="col">Role</th>
                <th scope="col">TO</th>
                <th scope="col">SJ</th>
                <th scope="col">TJ</th>
                <th scope="col">Total</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <tr key={row.tmsPersonId} data-testid={`tech-${row.tmsPersonId}`}>
                  <td className="cell-name">
                    <Link
                      className="rowlink"
                      to={`/officials/${encodeURIComponent(row.tmsPersonId)}`}
                    >
                      <span className={genderClass(row.gender)}>{row.name}</span>
                    </Link>
                  </td>
                  <td className="cell-ctry num">{row.country}</td>
                  <td className="cell-role">{row.role}</td>
                  <Count done={row.completed.to} appointed={row.to} />
                  <Count done={row.completed.sj} appointed={row.sj} />
                  <Count done={row.completed.tj} appointed={row.tj} />
                  <td className="cell-tot">
                    {row.completed.total}/{row.total}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pending ? null : (
        <div className="pager" aria-label="Technical official pages">
          <span className="pager__info num">
            Page {current} of {pageCount} · {filtered.length} officials
          </span>
          <button
            type="button"
            aria-label="Previous technical officials"
            disabled={current <= 1}
            onClick={() => {
              onPage(current - 1);
            }}
          >
            &lsaquo; Prev
          </button>
          <button
            type="button"
            aria-label="Next technical officials"
            disabled={current >= pageCount}
            onClick={() => {
              onPage(current + 1);
            }}
          >
            Next &rsaquo;
          </button>
        </div>
      )}

      <div className="panel__foot">
        <b>Only TOs and Judges</b>, as §10 states — no TD, ATDs, UMs or Umpires. Counts are of
        the whole tournament — every appointment stored against the schedule, drafts included.
        The per-day detail, match by match, is in{" "}
        <Link className="linkish" to="/appointments/technical">
          TO Appointments
        </Link>
        .
      </div>
    </section>
  );
}

/**
 * §10's second panel, verbatim: tabs `All` / `Male` / `Female`, columns `Umpire Name` · `Ctry`
 * · `FU` · `RU` · `VU` · `Total`, `completed / appointed` throughout, **only Umpires**.
 *
 * Built in RUN 53 against the store module 02 already publishes. `FU` is `U1 + U2` — §B8's
 * `On-Field` — because §10 names one on-field column and the engine keeps one on-field ledger.
 */
export function UmpiresSummary({
  rows,
  gender,
  onGender,
  page,
  onPage,
  loading = false,
  failure = null,
}: {
  rows: readonly UmpireWorkloadRow[];
  gender: UmpireGenderFilter;
  onGender: (gender: UmpireGenderFilter) => void;
  page: number;
  onPage: (page: number) => void;
  loading?: boolean;
  failure?: Error | null;
}): React.JSX.Element {
  const filtered = rows.filter((row) => gender === "all" || row.gender === gender);
  const pageCount = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const current = Math.min(page, pageCount);
  const shown = filtered.slice((current - 1) * ROWS_PER_PAGE, current * ROWS_PER_PAGE);
  const pending = loading || failure !== null;

  return (
    <section className="panel" aria-labelledby="umpsumTitle">
      <div className="panel__head">
        <h3 className="panel__title" id="umpsumTitle">
          Umpires Appointments
        </h3>
        <span className="panel__note">{TOURNAMENT_SCOPE_LABEL}</span>
        <span className="spacer" />
        <Tabs
          label="Umpires gender filter"
          options={UMPIRE_GENDER_TABS}
          value={gender}
          onChange={onGender}
        />
      </div>

      {pending ? (
        <PanelState loading={loading} failure={failure} />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No Umpires"
          detail="No Umpire is on the roster for this tournament under this filter, so there is no workload to summarise."
        />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="mtable" aria-label="Umpire appointments">
            <thead>
              <tr>
                <th scope="col">Umpire Name</th>
                <th scope="col">Ctry</th>
                <th scope="col">FU</th>
                <th scope="col">RU</th>
                <th scope="col">VU</th>
                <th scope="col">Total</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <tr key={row.tmsPersonId} data-testid={`ump-${row.tmsPersonId}`}>
                  <td className="cell-name">
                    <Link
                      className="rowlink"
                      to={`/officials/${encodeURIComponent(row.tmsPersonId)}`}
                    >
                      <span className={genderClass(row.gender)}>{row.name}</span>
                    </Link>
                  </td>
                  <td className="cell-ctry num">{row.country}</td>
                  <Count done={row.completed.fu} appointed={row.fu} />
                  <Count done={row.completed.ru} appointed={row.ru} />
                  <Count done={row.completed.vu} appointed={row.vu} />
                  <td className="cell-tot">
                    {row.completed.total}/{row.total}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pending ? null : (
        <div className="pager" aria-label="Umpire pages">
          <span className="pager__info num">
            Page {current} of {pageCount} · {filtered.length} officials
          </span>
          <button
            type="button"
            aria-label="Previous umpires"
            disabled={current <= 1}
            onClick={() => {
              onPage(current - 1);
            }}
          >
            &lsaquo; Prev
          </button>
          <button
            type="button"
            aria-label="Next umpires"
            disabled={current >= pageCount}
            onClick={() => {
              onPage(current + 1);
            }}
          >
            Next &rsaquo;
          </button>
        </div>
      )}

      <div className="panel__foot">
        <b>Only Umpires</b>, as §10 states — no TD, ATDs, TOs, Judges or UMs. Counts are of the
        whole tournament, from the Umpires Managers&rsquo; own appointments (§B1); the advisory
        AI half is a yardstick and is never counted here. The per-match detail is in{" "}
        <Link className="linkish" to="/appointments/umpires">
          Umpire Appointments
        </Link>
        .
      </div>
    </section>
  );
}
